// frontend/src/utils/textHelpers.ts
/**
 * Text formatting utilities
 */

/**
 * Capitalize the first letter of a string
 * @param str - String to capitalize
 * @returns Capitalized string
 * 
 * @example
 * capitalizeFirst('explorer') // 'Explorer'
 * capitalizeFirst('professional') // 'Professional'
 * capitalizeFirst('ADMIN') // 'Admin'
 */
export function capitalizeFirst(str: string | undefined | null): string {
  if (!str) return '';
  
  // Convert to lowercase first, then capitalize first letter
  const lower = str.toLowerCase().trim();
  if (lower.length === 0) return '';
  
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Decode the small set of HTML entities produced by the API input sanitizer.
 * React still escapes the resulting strings when rendering them as text.
 */
export function decodeHtmlEntities(text: string): string {
  let decoded = String(text || '');

  // Older sanitized rows can contain one entity encoded inside another
  // (for example, &amp;#x2F;). Decode a bounded number of layers while React
  // continues to escape the final string when it is rendered as text.
  for (let pass = 0; pass < 3; pass += 1) {
    const next = decoded
      .replace(/&amp;/gi, '&')
      .replace(/&#x27;/gi, "'")
      .replace(/&#39;/g, "'")
      .replace(/&#x2f;/gi, '/')
      .replace(/&#47;/g, '/')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&quot;/gi, '"')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>');

    if (next === decoded) break;
    decoded = next;
  }

  return decoded;
}

/**
 * Normalize JSON response text without changing numbers, booleans, or nulls.
 */
export function decodeHtmlEntitiesDeep<T>(value: T): T {
  if (typeof value === 'string') {
    return decodeHtmlEntities(value) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => decodeHtmlEntitiesDeep(item)) as T;
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        decodeHtmlEntitiesDeep(item),
      ])
    ) as T;
  }

  return value;
}

/**
 * Format plan name for display (capitalizes first letter)
 * Also maps Wix plan names to our internal tier slugs
 * @param planName - Plan name from database or API
 * @returns Formatted plan name
 * 
 * Public plan display:
 * - Explorer - free founder preview
 * - Builder - founder plan
 * - Organization - program/cohort pilot
 * - Support role - legacy consultant/advisor/investor support labels
 * 
 * @example
 * formatPlanName('explorer') // 'Explorer'
 * formatPlanName('free') // 'Explorer' (mapped from Wix)
 * formatPlanName('builder') // 'Builder'
 * formatPlanName('professional') // 'Builder' (legacy mapping)
 */
export function formatPlanName(planName: string | undefined | null): string {
  if (!planName) return '';
  
  // 🎯 Map plan names to new tier structure (Dec 15, 2025)
  const planMapping: Record<string, string> = {
    // Free tier mappings
    'free': 'Explorer',
    'trial': 'Explorer',
    'Free': 'Explorer',
    'FREE': 'Explorer',
    'explorer': 'Explorer',
    // Builder tier mappings (legacy: professional/pro)
    'builder': 'Builder',
    'professional': 'Builder',
    'pro': 'Builder',
    // Organization pilot mappings (legacy: teams/enterprise/executive)
    'organization': 'Organization',
    'teams': 'Organization',
    'enterprise': 'Organization',
    'executive': 'Organization',
    'corporate': 'Organization',
    'custom': 'Organization',
    // Support role mappings. These are not current public plans.
    'advisor': 'Support role',
    'advisors': 'Support role',
    'consultant': 'Support role',
    'mentor': 'Support role',
    'investor': 'Support role',
  };
  
  // Apply mapping if exists, otherwise capitalize the original
  return planMapping[planName.toLowerCase()] || capitalizeFirst(planName);
}
